import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'use-intl';
import type { Locale } from '@/i18n';
import {
  useConfig,
  useDepthChart,
  useSaveDepthChart,
  type DevelopmentSquadRow,
} from '@/app/queries';
import { sheetName } from '@/lib/format';
import { configLabel } from '@/lib/labels';
import { FORMATIONS, unitOf } from '@shared/formations';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea } from '@/ui/Field';
import { EmptyState, Notice, Panel } from '@/ui/Panel';
import { Chip, PositionChip } from '@/ui/Chip';

const ASSESSMENTS = ['strong', 'adequate', 'thin', 'urgent'] as const;

interface SlotState {
  positionKey: string;
  label: string | null;
  assessment: string | null;
  note: string | null;
  entries: { playerId: string }[];
}

/** How a suggestion is drawn: an opinion, in the same ink as any other note. */
const tone: Record<string, string> = {
  strong: 'text-ok',
  adequate: 'text-ink-muted',
  thin: 'text-signal',
  urgent: 'text-risk',
};

export function Depth({ locale: _locale, teamId }: { locale: Locale; teamId: string }) {
  const t = useTranslations();
  const chart = useDepthChart(teamId);
  const config = useConfig();
  const save = useSaveDepthChart(teamId);

  const [formationKey, setFormationKey] = useState('4-2-3-1');
  const [notes, setNotes] = useState('');
  const [slots, setSlots] = useState<SlotState[]>([]);
  const [dirty, setDirty] = useState(false);

  const squad = chart.data?.squad ?? [];
  const positions = config.data?.positions ?? [];
  const positionsByKey = useMemo(
    () => new Map(positions.map((p) => [p.key, p])),
    [positions],
  );
  const playersById = useMemo(
    () => new Map(squad.map((p) => [p.playerId, p])),
    [squad],
  );

  useEffect(() => {
    if (!chart.data?.chart) return;
    setFormationKey(chart.data.chart.formationKey);
    setNotes(chart.data.chart.notes ?? '');
    setSlots(
      chart.data.slots.map((s) => ({
        positionKey: s.positionKey,
        label: s.label,
        assessment: s.assessment,
        note: s.note,
        entries: s.entries.map((e) => ({ playerId: e.playerId })),
      })),
    );
    setDirty(false);
  }, [chart.data]);

  const suggestions = useMemo(
    () => new Map((chart.data?.slots ?? []).map((s) => [s.positionKey, s.suggestion])),
    [chart.data?.slots],
  );

  /**
   * Rebuilding on a shape change keeps the rankings of positions that survive.
   * A coach moving 4-2-3-1 to 4-3-3 has not changed their mind about who the
   * right back is.
   */
  const reshape = (key: string) => {
    const formation = FORMATIONS.find((f) => f.key === key);
    if (!formation) return;
    const kept = new Map(slots.map((s) => [s.positionKey, s]));
    setFormationKey(key);
    setSlots(
      formation.slots.map(
        (slot) =>
          kept.get(slot.positionKey) ?? {
            positionKey: slot.positionKey,
            label: null,
            assessment: null,
            note: null,
            entries: [],
          },
      ),
    );
    setDirty(true);
  };

  const assigned = useMemo(
    () => new Set(slots.flatMap((s) => s.entries.map((e) => e.playerId))),
    [slots],
  );

  const alsoAt = useMemo(() => {
    const out = new Map<string, string[]>();
    for (const s of slots) {
      for (const e of s.entries) {
        const list = out.get(e.playerId) ?? [];
        list.push(s.positionKey);
        out.set(e.playerId, list);
      }
    }
    return out;
  }, [slots]);

  const mutate = (index: number, change: (slot: SlotState) => SlotState) => {
    setSlots((current) => current.map((s, i) => (i === index ? change(s) : s)));
    setDirty(true);
  };

  const commit = async () => {
    await save.mutateAsync({
      formationKey,
      notes: notes || null,
      slots: slots.map((s) => ({
        positionKey: s.positionKey,
        label: s.label,
        assessment: s.assessment,
        note: s.note,
        // Rank is the list order: coaches rank, they do not score.
        entries: s.entries.map((e, i) => ({ playerId: e.playerId, rank: i + 1 })),
      })),
    });
    setDirty(false);
  };

  if (chart.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (!chart.data?.chart) {
    return (
      <Panel title={t('depth.title')}>
        <EmptyState
          title={t('depth.empty')}
          body={t('depth.emptyBody')}
          action={
            <div className="flex flex-wrap items-end justify-center gap-3">
              <div className="w-48 text-start">
                <Field label={t('depth.formation')}>
                  {(p) => (
                    <SelectInput
                      {...p}
                      value={formationKey}
                      onChange={(e) => setFormationKey(e.target.value)}
                    >
                      {FORMATIONS.map((f) => (
                        <option key={f.key} value={f.key}>
                          {f.key}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </Field>
              </div>
              <Button
                variant="primary"
                busy={save.isPending}
                onClick={() => void save.mutateAsync({ formationKey })}
              >
                {t('depth.create')}
              </Button>
            </div>
          }
        />
      </Panel>
    );
  }

  const unassigned = squad.filter((p) => !assigned.has(p.playerId));

  return (
    <div className="grid gap-5">
      <Panel
        title={t('depth.title')}
        explain="depth_cover"
        count={t('depth.lead')}
        action={
          <Button variant="primary" busy={save.isPending} disabled={!dirty} onClick={() => void commit()}>
            {t('depth.save')}
          </Button>
        }
      >
        <div className="grid gap-x-5 gap-y-2 p-4 sm:grid-cols-3">
          <Field label={t('depth.formation')} hint={t('depth.reshape')}>
            {(p) => (
              <SelectInput {...p} value={formationKey} onChange={(e) => reshape(e.target.value)}>
                {FORMATIONS.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.key}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>
          <div className="sm:col-span-2">
            <Field label={t('depth.notes')} optional>
              {(p) => (
                <TextArea
                  {...p}
                  rows={2}
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    setDirty(true);
                  }}
                />
              )}
            </Field>
          </div>
        </div>
      </Panel>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {slots.map((slot, index) => {
          const position = positionsByKey.get(slot.positionKey);
          const suggestion = suggestions.get(slot.positionKey);
          const available = squad.filter(
            (p) => !slot.entries.some((e) => e.playerId === p.playerId),
          );
          return (
            <section
              key={`${slot.positionKey}-${index}`}
              className="rounded-(--radius-panel) border border-line bg-surface"
            >
              <header className="flex items-center gap-2 border-b border-line px-3 py-2">
                <PositionChip code={slot.positionKey} unit={unitOf(slot.positionKey)} />
                <span className="min-w-0 flex-1 truncate font-condensed text-subhead font-semibold">
                  {slot.label ?? configLabel(position, t)}
                </span>
                <span className="tabular text-meta text-ink-faint">{slot.entries.length}</span>
              </header>

              <ol className="divide-y divide-line">
                {slot.entries.map((entry, rank) => {
                  const p = playersById.get(entry.playerId);
                  const elsewhere = (alsoAt.get(entry.playerId) ?? []).filter(
                    (k) => k !== slot.positionKey,
                  );
                  return (
                    <li key={entry.playerId} className="flex items-center gap-2 px-3 py-2">
                      <span className="tabular w-5 shrink-0 font-condensed font-semibold text-ink-muted">
                        {rank + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-meta font-medium">
                          {p ? sheetName(p.knownAs ?? p.firstName, p.lastName) : entry.playerId}
                        </span>
                        {elsewhere.length > 0 ? (
                          <span className="block truncate text-meta text-ink-faint">
                            {t('depth.alsoAt', { positions: elsewhere.join(', ') })}
                          </span>
                        ) : null}
                      </span>
                      <span className="flex shrink-0 gap-0.5">
                        <button
                          type="button"
                          aria-label={`${t('depth.rank')} −`}
                          disabled={rank === 0}
                          onClick={() =>
                            mutate(index, (s) => {
                              const next = [...s.entries];
                              [next[rank - 1]!, next[rank]!] = [next[rank]!, next[rank - 1]!];
                              return { ...s, entries: next };
                            })
                          }
                          className="min-h-8 px-1.5 text-ink-faint hover:text-ink disabled:opacity-30"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          aria-label={`${t('depth.rank')} +`}
                          disabled={rank === slot.entries.length - 1}
                          onClick={() =>
                            mutate(index, (s) => {
                              const next = [...s.entries];
                              [next[rank]!, next[rank + 1]!] = [next[rank + 1]!, next[rank]!];
                              return { ...s, entries: next };
                            })
                          }
                          className="min-h-8 px-1.5 text-ink-faint hover:text-ink disabled:opacity-30"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          aria-label={t('common.remove')}
                          onClick={() =>
                            mutate(index, (s) => ({
                              ...s,
                              entries: s.entries.filter((e) => e.playerId !== entry.playerId),
                            }))
                          }
                          className="min-h-8 px-1.5 text-ink-faint hover:text-risk"
                        >
                          ×
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ol>

              <div className="grid gap-2 border-t border-line px-3 py-2">
                <SelectInput
                  aria-label={t('depth.addPlayer')}
                  value=""
                  onChange={(e) => {
                    const id = e.target.value;
                    if (!id) return;
                    mutate(index, (s) => ({ ...s, entries: [...s.entries, { playerId: id }] }));
                  }}
                >
                  <option value="">{t('depth.addPlayer')}</option>
                  {available.map((p) => (
                    <option key={p.playerId} value={p.playerId}>
                      {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                    </option>
                  ))}
                </SelectInput>

                <SelectInput
                  aria-label={t('depth.assessment')}
                  value={slot.assessment ?? ''}
                  onChange={(e) =>
                    mutate(index, (s) => ({ ...s, assessment: e.target.value || null }))
                  }
                >
                  <option value="">{t('depth.assessment')}</option>
                  {ASSESSMENTS.map((a) => (
                    <option key={a} value={a}>
                      {t(`depth.assessmentValue.${a}`)}
                    </option>
                  ))}
                </SelectInput>

                {suggestion ? (
                  <p className="text-meta text-ink-faint">
                    <span className={tone[suggestion.suggestion] ?? ''}>
                      {t('depth.suggested', {
                        value: t(`depth.assessmentValue.${suggestion.suggestion}`),
                      })}
                    </span>
                    {' — '}
                    {t(suggestion.reason)}
                    {'. '}
                    {t('depth.cover', {
                      cover: suggestion.cover.toFixed(1),
                      players: suggestion.players,
                    })}
                  </p>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>

      <Panel title={t('depth.unassigned')} count={unassigned.length}>
        {unassigned.length === 0 ? (
          <p className="px-4 py-4 text-meta text-ink-muted">{t('depth.unassignedHint')}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 p-4">
            {unassigned.map((p: DevelopmentSquadRow) => (
              <Chip key={p.playerId} muted>
                {p.shirtNumber ? <span className="tabular me-1">{p.shirtNumber}</span> : null}
                {sheetName(p.knownAs ?? p.firstName, p.lastName)}
                {p.positionKey ? <span className="ms-1 text-ink-faint">{p.positionKey}</span> : null}
              </Chip>
            ))}
          </div>
        )}
      </Panel>

      {dirty ? (
        <div className="sticky bottom-3">
          <Notice tone="info">{t('common.unsaved')}</Notice>
        </div>
      ) : null}
    </div>
  );
}
